import React, { useRef, useEffect, useState } from 'react';
import SignaturePad from 'signature_pad';
import { X } from 'lucide-react';

interface SignatureCanvasProps {
  onChange: (base64: string) => void;
  onSignatureFlagChange?: (signed: boolean) => void;
  initialSignature?: string;
}

const SignatureCanvas: React.FC<SignatureCanvasProps> = ({
  onChange,
  onSignatureFlagChange,
  initialSignature,
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const signaturePadRef = useRef<SignaturePad | null>(null);
  const [isSigned, setIsSigned] = useState(!!initialSignature);

  const hasMinimalSignature = (signaturePad: SignaturePad): boolean => {
    if (!signaturePad.isEmpty()) {
      return true;
    }

    try {
      const data = signaturePad.toData();

      if (data && data.length > 0) {
        for (const stroke of data) {
          if (stroke.points && stroke.points.length > 0) {
            return true;
          }
        }
      }

      const dataURL = signaturePad.toDataURL();
      if (
        dataURL
      ) {
        return true;
      }
    } catch (error) {
      console.warn('Error checking signature data:', error);
    }

    return false;
  };

  // Обновляем состояние подписи при изменении initialSignature
  useEffect(() => {
    if (initialSignature && initialSignature.trim() !== '') {
      setIsSigned(true);
      // Если у нас уже есть signaturePad, загружаем подпись
      if (signaturePadRef.current) {
        try {
          signaturePadRef.current.fromDataURL(initialSignature);
        } catch (error) {
          console.warn('Error loading signature:', error);
          // Если не удалось загрузить, очищаем
          signaturePadRef.current.clear();
          setIsSigned(false);
        }
      }
    } else {
      setIsSigned(false);
      // Очищаем canvas если нет подписи
      if (signaturePadRef.current) {
        signaturePadRef.current.clear();
      }
    }
  }, [initialSignature]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ratio = Math.max(window.devicePixelRatio || 1, 1);
    canvas.width = canvas.offsetWidth * ratio;
    canvas.height = canvas.offsetHeight * ratio;
    const ctx = canvas.getContext('2d');
    if (ctx) ctx.scale(ratio, ratio);

    const signaturePad = new SignaturePad(canvas, {
      penColor: 'black',
    });
    signaturePadRef.current = signaturePad;

    // Загружаем начальную подпись если она есть
    if (initialSignature) {
      signaturePad.fromDataURL(initialSignature);
      setIsSigned(true);
    }

    const handlePointerUp = () => {
      if (!signaturePadRef.current) return;

      // Используем нашу улучшенную функцию проверки
      const signed = hasMinimalSignature(signaturePadRef.current);
      setIsSigned(signed);
      onSignatureFlagChange?.(signed);
      if(signaturePadRef.current.toDataURL()){
        onChange(signed ? signaturePadRef.current.toDataURL() : '');
      }
    };

    const handlePointerMove = () => {
      if (!signaturePadRef.current) return;

      // Проверяем подпись во время движения для более быстрой реакции
      const signed = hasMinimalSignature(signaturePadRef.current);
      if (signed !== isSigned) {
        setIsSigned(signed);
        onSignatureFlagChange?.(signed);
        onChange(signed ? signaturePadRef.current.toDataURL() : '');
      }
    };

    canvas.addEventListener('pointerup', handlePointerUp);
    canvas.addEventListener('pointermove', handlePointerMove);

    return () => {
      canvas.removeEventListener('pointerup', handlePointerUp);
      canvas.removeEventListener('pointermove', handlePointerMove);
      signaturePadRef.current?.off();
      signaturePadRef.current = null;
    };
  }, []);

  const clearCanvas = () => {
    if (signaturePadRef.current) {
      signaturePadRef.current.clear();
      setIsSigned(false);
      onSignatureFlagChange?.(false);
      onChange('');
    }
  };

  return (
    <div className="w-full relative h-full">
      <canvas ref={canvasRef} style={{ width: '100%', height: '100%' }} />
      {isSigned && (
        <button
          onClick={clearCanvas}
          className="absolute top-2 right-2 cursor-pointer p-1 text-gray-400 rounded-full hover:text-primary transition"
          aria-label="Clear signature"
        >
          <X size={16} />
        </button>
      )}
    </div>
  );
};

export default SignatureCanvas;